// Actions
import { ActionType } from "@ms/planner-actions/lib/local/ActionType";
import { isEntityAction } from "@ms/planner-actions/lib/local/utils/ActionUtils";
// Constants
import { projectManagerAgentId } from "@ms/uno-constants/lib/local/AgentConstants";
import { TaskChartMetric } from "@ms/uno-constants/lib/local/ChartConstants";
import { EmptyTaskChartMetrics, TaskGroupingOption, UnassignedTaskGroup } from "@ms/uno-constants/lib/local/GroupingConstants";
import { ViewType } from "@ms/uno-constants/lib/local/RoutingConstants";
import { PlannerDefaultPlanId } from "@ms/uno-constants/lib/local/ServiceConstants";
import { StoreEntityState, TableName } from "@ms/uno-constants/lib/local/StoreConstants";
import { ViewDisplayState, ViewName } from "@ms/uno-constants/lib/local/ViewConstants";
import { MyTasksListViewPartitionSuffix, MyTasksViewPivotType } from "@ms/uno-constants/lib/local/ViewPivots";
import { applyComparator, applyFilter, registerIndex } from "@ms/uno-entitycore/lib/local/table/EntityTable";
import { Table } from "@ms/uno-entitycore/lib/local/table/Table";
import { DefaultPlanSource } from "@ms/planner-models/lib/local/client/planner/DefaultPlanSource";
import { QueryRelationship } from "@ms/planner-models/lib/local/client/planner/EntityRelationships";
import { EntityType } from "@ms/planner-models/lib/local/client/planner/EntityType";
import { TaskKind } from "@ms/planner-models/lib/local/client/planner/task/ITask";
import { CustomFieldValueType } from "@ms/planner-models/lib/local/service/planner/enumerations/IPlannerEnumerations";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { generateLogStringFromError } from "@ms/uno-telemetry/lib/local/utilities/LogUtilities";
import { Temporal } from "temporal-polyfill-lite";
// Stores
import { EventPublishingStore } from "@ms/uno-fluxcore/lib/local/stores/EventPublishingStore";
import { EntitySlice } from "../../slice/EntitySlice";
import { Slice } from "../../slice/Slice";
import { Store } from "../Store";
// Utilities
import { resolveCustomFieldDefinitionIdFromGroupingOption, toCustomFieldGroupingOption } from "@ms/planner-models/lib/local/utilities/CustomFieldDefinitionUtilities";
import { getTaskChartMetric } from "@ms/planner-models/lib/local/utilities/TaskChartUtilities";
import { applyEntityFilter } from "@ms/planner-models/lib/local/utilities/TaskFilteringUtilities";
import { getTaskGroups } from "@ms/planner-models/lib/local/utilities/TaskGroupingUtilities";
import { arrangeTasksInHierarchicalOrder, collectAncestorIds } from "@ms/planner-models/lib/local/utilities/TaskHierarchyUtilities";
import { compareByDueDate, compareByName, compareByStartDate, getDefaultTasksSortComparator_PlanView } from "@ms/planner-models/lib/local/utilities/TaskSortingUtilities";
import { AllTasksGroup, PrivateTasksVirtualGroupKey } from "@ms/uno-constants/lib/local/GroupingConstants";
import { compareArraysEqualAsSet, compareByOrderHint } from "@ms/uno-utilities/lib/local/Comparators";
import { getSystemTimeZone, getTodayPlainDate } from "@ms/uno-utilities/lib/local/DateUtilities-temporal";
import { toViewIdString } from "@ms/uno-utilities/lib/local/ViewUtilities";
import { logUnreachableAction } from "../../utilities/LoggingUtilities";
import { AbsentStoreEntityStatus, isStateFailure } from "../../utilities/StatusUtilities";
import { filterPersistedTablesForStore } from "../../utilities/StoreUtilities";
// Index for tracking chart metrics for a task partition
const ChartMetricIndex = "chartMetric";
// Root view index name
const ViewIndex = "view";
// Index for tracking MyTasksView pivots
const MyTasksViewPivotIndex = "myTasksViewPivot";
// Index for tracking child tasks by their parent task id within a plan partition
const ParentTaskIndex = "parentTask";
// Partition key for root tasks in the parent-task index
const RootTaskIndexKey = "root";
// View partition keys for personal views
const MyTasksViewPartition = "myTasksView";
const MyDayViewPartition = "myDayView";
export class TaskStore extends EventPublishingStore {
    get name() {
        return Store.Tasks;
    }
    // #region GETTERS
    // #region DATA
    getPersistedTables() {
        return filterPersistedTablesForStore(Store.Tasks, {
            [TableName.Tasks]: this.taskSlice.table(),
            ...this.taskSlice.statusTables()
        });
    }
    getTask(id, readBaseline) {
        return this.taskSlice.get(id, readBaseline);
    }
    getDefaultPlanStatus(source) {
        return this.defaultPlanStatuses[source] ?? AbsentStoreEntityStatus;
    }
    getTasksForPlan(planId) {
        // Private Tasks is addressed by the well-known alias and spans both backing plans, so the alias resolves to
        // their union rather than to a single plan. Every other id is a real plan and reads its own collection.
        if (planId === PlannerDefaultPlanId) {
            return this.getDefaultPlanTasks();
        }
        return this.taskSlice.getCollection({
            name: QueryRelationship.TasksInPlan,
            value: planId
        });
    }
    /**
     * Reads the tasks of every plan that currently backs Private Tasks.
     *
     * Both backings can hold tasks at once during rollout - a user keeps the To Do tasks they had before the flight
     * turned on - so this is a union rather than a switch on the active source, and it degrades per source: the
     * result is readable as soon as either backing is, because one unresolved source must not stall the pivot.
     * @returns The union of both backing plans' tasks, deduped by task id, with a combined read status
     */ getDefaultPlanTasks() {
        const sources = [
            DefaultPlanSource.ToDo
        ];
        if (this.configProvider().flights.EnableMyTasksDefaultPlan) {
            sources.push(DefaultPlanSource.Planner);
        }
        const seenTaskIds = new Set();
        const tasks = [];
        const statuses = [];
        // A source is only ready once its default plan resolved AND that plan's tasks were fetched.
        let hasReadySource = false;
        for (const source of sources){
            const planId = this.getDefaultPlanId(source);
            statuses.push(this.getDefaultPlanStatus(source));
            const result = planId ? this.getTasksForPlan(planId) : {
                data: [],
                status: AbsentStoreEntityStatus
            };
            if (planId) {
                statuses.push(result.status);
                hasReadySource = hasReadySource || result.status.state === StoreEntityState.Present;
            }
            for (const task of result.data ?? []){
                if (!seenTaskIds.has(task.id)) {
                    seenTaskIds.add(task.id);
                    tasks.push(task);
                }
            }
        }
        const failureStatus = statuses.find((status)=>isStateFailure(status.state));
        return {
            data: tasks,
            status: {
                isFetching: statuses.some((status)=>status.isFetching),
                // Render as soon as any source is ready. A source that has not resolved yet must not block the
                // pivot, otherwise one unresolved source leaves the view stuck in its loading state forever.
                state: failureStatus ? failureStatus.state : hasReadySource ? StoreEntityState.Present : StoreEntityState.Absent,
                timestamp: statuses.find((status)=>status.timestamp)?.timestamp ?? null
            }
        };
    }
    getLabelUsageCountsForPlan(planId) {
        return this.labelUsageCountsByPlan.get(planId) ?? TaskStore.EmptyLabelUsageCounts;
    }
    getAllTasksMapForPlan(planId) {
        const tasks = this.getTasksForPlan(planId).data ?? [];
        return new Map(tasks.map((task)=>[
                task.id,
                task
            ]));
    }
    getTasksForBucket(planId, bucketId) {
        const planPartition = this.taskTable.index(QueryRelationship.TasksInPlan).partition(planId);
        // Ensure that the plan partition has a bucket index
        // IMPORTANT: Given the index references planValidBucketIds table, we must refresh the index whenever valid ids change
        const timeZone = getSystemTimeZone();
        const todayPlainDate = getTodayPlainDate(timeZone, this.loggers.traceLogger);
        registerIndex(planPartition, EntityType.Bucket, (task)=>getTaskGroups(task, TaskGroupingOption.Bucket, this.configProvider, this.planValidBucketIds.get(planId) ?? undefined, timeZone, todayPlainDate, this.planValidCfdValueTypes.get(planId) ?? undefined));
        return planPartition.index(EntityType.Bucket).partition(bucketId).items().map((item)=>item.value);
    }
    getTaskIndexMapForPlan(planId) {
        const items = this.taskTable.index(QueryRelationship.TasksInPlan).partition(planId).items();
        const indexMap = new Map();
        for(let i = 0; i < items.length; i++){
            indexMap.set(items[i].value.id, i + 1);
        }
        return indexMap;
    }
    getTasksForGoal(planId, goalId) {
        const planPartition = this.taskTable.index(QueryRelationship.TasksInPlan).partition(planId);
        // Ensure that the plan partition has a goal index
        // IMPORTANT: Given the index references planValidGoalIds table, we must refresh the index whenever valid ids change
        const timeZone = getSystemTimeZone();
        const todayPlainDate = getTodayPlainDate(timeZone, this.loggers.traceLogger);
        registerIndex(planPartition, EntityType.Goal, (task)=>getTaskGroups(task, TaskGroupingOption.Goal, this.configProvider, this.planValidGoalIds.get(planId) ?? undefined, timeZone, todayPlainDate, this.planValidCfdValueTypes.get(planId) ?? undefined));
        return planPartition.index(EntityType.Goal).partition(goalId).items().map((item)=>item.value);
    }
    getRootTasks(planId) {
        return this.getTasksForParentTaskIndex(planId, RootTaskIndexKey);
    }
    getChildTasks(planId, parentTaskId) {
        if (!this.configProvider().flights.EnableTaskHierarchy) {
            return [];
        }
        return this.getTasksForParentTaskIndex(planId, parentTaskId);
    }
    getTasksAssignedToUser(userId) {
        return this.taskSlice.getCollection({
            name: QueryRelationship.TasksAssignedToUser,
            value: userId
        });
    }
    getMyDayTasks() {
        return this.taskSlice.getCollection({
            name: QueryRelationship.TasksInMyDayOfUser,
            value: this.user.id
        });
    }
    getLoopPageUrlForTask(taskId) {
        return this.loopPageUrlSlice.get(taskId);
    }
    isBulkEditInProgress() {
        return this.isBulkEditOngoing;
    }
    getInProgressUploadsForTask(taskId) {
        // Fall back to the shared frozen sentinel so callers without uploads always see the
        // same array reference — prevents unnecessary re-renders in memoized consumers.
        return this.inProgressUploadsByTask.get(taskId) ?? TaskStore.EmptyUploads;
    }
    getBaselineChainPair(id, deltaKey) {
        return this.taskSlice.getBaselineChainPair(id, deltaKey);
    }
    // #endregion
    // #region VIEW
    getTasksViewDisplayState(viewId) {
        // We can use Grid ViewType always as we just need to check if we have any tasks for the view and the same set of tasks show up in every viewType
        if (this.getTasksViewPartition(viewId, ViewType.Grid).itemIds().length !== 0) {
            return ViewDisplayState.DataAvailable;
        }
        const tasksDataPartition = this.getTasksDataPartition(viewId);
        if (tasksDataPartition.status.state === StoreEntityState.Absent) {
            return ViewDisplayState.DataNotFetched;
        }
        if (tasksDataPartition.data.length === 0) {
            return ViewDisplayState.DataNotAvailable;
        }
        return ViewDisplayState.DataAvailableButFiltered;
    }
    getFilteredTaskCount(viewId) {
        // Get the filtered partition for the current view or My Tasks pivot.
        const viewIndex = this.taskTable.index(ViewIndex);
        let taskPartition;
        switch(viewId.name){
            case ViewName.MyDayPage:
                taskPartition = viewIndex.partition(MyDayViewPartition);
                break;
            case ViewName.MyTasksPage:
                {
                    const pivot = viewId.partitionKey.replace(MyTasksListViewPartitionSuffix, "");
                    taskPartition = viewIndex.partition(MyTasksViewPartition).index(MyTasksViewPivotIndex).partition(pivot);
                    break;
                }
            case ViewName.PlanPage:
                taskPartition = viewIndex.partition(viewId.partitionKey);
                break;
            default:
                return 0;
        }
        return taskPartition.itemIds().length;
    }
    getTasksForGridView(viewId) {
        const tasks = this.getTasksViewPartition(viewId, ViewType.Grid).items().map((item)=>item.value);
        if (viewId.name === ViewName.PlanPage && this.configProvider().flights.EnableTaskHierarchy) {
            return arrangeTasksInHierarchicalOrder(tasks, (parentTaskId)=>this.getChildTasks(viewId.partitionKey, parentTaskId));
        } else {
            return this.toFlatHierarchicalResult(tasks);
        }
    }
    /**
     * Read a view's partition as a flat list in the partition's natural (comparator) order.
     * Each view type owns its own partition, so callers must pass the type they registered.
     */ getOrderedTasksForView(viewId, viewType) {
        return this.getTasksViewPartition(viewId, viewType).items().map((item)=>item.value);
    }
    getTasksForCalendarView(viewId) {
        return this.getOrderedTasksForView(viewId, ViewType.Schedule);
    }
    getTasksForTimelineView(viewId) {
        const tasks = [
            ...this.getTasksForGridView(viewId).flattenedTasks
        ].sort(getDefaultTasksSortComparator_PlanView());
        return this.toFlatHierarchicalResult(tasks);
    }
    getTasksForTeamView(viewId) {
        const teamViewPartition = this.getTasksViewPartition(viewId, ViewType.People);
        // Reuses the shared assignee grouping used by the board and chart views so Team View bands stay
        // consistent with the rest of the product, minus the Project Manager agent. Team View bands are
        // people: the agent is not a directory user, so it would resolve to a nameless band whose drop
        // target attempts an unsupported assignment. Dropping it here (rather than in the view) means a
        // task assigned only to the agent falls through to Unassigned like any other unassigned task,
        // and the layout never has to know the agent exists.
        registerIndex(teamViewPartition, TaskGroupingOption.Assignee, (task)=>{
            const groups = getTaskGroups(task, TaskGroupingOption.Assignee, this.configProvider);
            const memberGroups = (Array.isArray(groups) ? groups : [
                groups
            ]).filter((groupId)=>groupId !== projectManagerAgentId);
            return memberGroups.length > 0 ? memberGroups : UnassignedTaskGroup;
        });
        const assigneeIndex = teamViewPartition.index(TaskGroupingOption.Assignee);
        return assigneeIndex.keys().reduce((acc, groupId)=>{
            acc[groupId] = assigneeIndex.partition(groupId).items().map((item)=>item.value);
            return acc;
        }, {});
    }
    getTasksForBoardView(viewId, groupBy) {
        const boardViewPartition = this.getTasksViewPartition(viewId, ViewType.Board);
        // Ensure that the grouping index is registered.
        // Read the valid-ids set inside the closure so refreshIndex() picks up the latest set
        // instead of a stale snapshot. Map only the grouping options that need a valid-id set;
        // all others fall through to undefined.
        const validIdsByGroupBy = {
            [TaskGroupingOption.Bucket]: this.planValidBucketIds,
            [TaskGroupingOption.Goal]: this.planValidGoalIds
        };
        const timeZone = getSystemTimeZone();
        const todayPlainDate = getTodayPlainDate(timeZone, this.loggers.traceLogger);
        // Rebuild the grouping index if the day changed, so date-based columns don't stay frozen.
        this.dropStaleDateGroupingIndex(boardViewPartition, viewId, groupBy, todayPlainDate);
        registerIndex(boardViewPartition, groupBy, (task)=>{
            if (groupBy === TaskGroupingOption.Plan && (viewId.name === ViewName.MyTasksPage || viewId.name === ViewName.MyDayPage) && this.isDefaultPlan(task.planId)) {
                return PrivateTasksVirtualGroupKey;
            }
            return this.groupTaskSafely(groupBy, ()=>getTaskGroups(task, groupBy, this.configProvider, validIdsByGroupBy[groupBy]?.get(viewId.partitionKey) ?? undefined, timeZone, todayPlainDate, this.planValidCfdValueTypes.get(viewId.partitionKey) ?? undefined));
        });
        const groupIds = boardViewPartition.index(groupBy).keys();
        return groupIds.reduce((acc, groupId)=>{
            acc[groupId] = this.getTasksForBoardViewGroup(viewId, groupBy, groupId);
            return acc;
        }, {});
    }
    /**
     * Applies a grouping callback that can reject the grouping option outright.
     *
     * `groupBy` here comes from persisted view state, so it can be an option this call site can't
     * satisfy - Source needs plan lookups only the My Tasks view supplies. Collapsing the index into
     * one group keeps the view rendering; logging is deduped per option because the index rebuilds
     * per task and the rejection doesn't depend on which one.
     */ groupTaskSafely(groupBy, getGroups) {
        try {
            return getGroups();
        } catch (e) {
            if (!this.loggedGroupingFailures.has(groupBy)) {
                this.loggedGroupingFailures.add(groupBy);
                this.loggers.traceLogger.logTrace(0x1d79e450 /* tag_344rq */ , TraceLevel.Error, `TaskGroupingFailed [GroupBy=${groupBy}] ${generateLogStringFromError(e)}`);
            }
            return AllTasksGroup;
        }
    }
    getTasksForBoardViewGroup(viewId, groupBy, groupId) {
        return this.getTasksViewPartition(viewId, ViewType.Board).index(groupBy).partition(groupId).items().map((item)=>item.value);
    }
    /**
     * Get tasks connected to a goal, filtered by the active plan task filter.
     * Unlike {@link getTasksForGoal}, which reads the raw plan partition, this reads the filtered
     * `ViewType.Goals` view partition so Goals view can match Board-grouped-by-Goal filtering behavior
     * without changing goal editing or task-selection surfaces, which must keep seeing every connected task.
     * @param viewId Id of the view.
     * @param goalId Id of the goal.
     */ getTasksForGoalsView(viewId, goalId) {
        const goalsViewPartition = this.getTasksViewPartition(viewId, ViewType.Goals);
        // Ensure that the goals view partition has a goal index
        // IMPORTANT: Given the index references planValidGoalIds table, we must refresh the index whenever valid ids change
        const timeZone = getSystemTimeZone();
        const todayPlainDate = getTodayPlainDate(timeZone, this.loggers.traceLogger);
        registerIndex(goalsViewPartition, EntityType.Goal, (task)=>getTaskGroups(task, TaskGroupingOption.Goal, this.configProvider, this.planValidGoalIds.get(viewId.partitionKey) ?? undefined, timeZone, todayPlainDate, this.planValidCfdValueTypes.get(viewId.partitionKey) ?? undefined));
        return goalsViewPartition.index(EntityType.Goal).partition(goalId).items().map((item)=>item.value);
    }
    getTaskMetricsForChartView(viewId) {
        const chartViewPartition = this.getChartViewPartition(viewId);
        if (!chartViewPartition) {
            return {
                ...EmptyTaskChartMetrics
            };
        }
        return this.getChartMetrics(chartViewPartition);
    }
    getTaskMetricsForChartViewGroup(viewId, groupBy) {
        const chartViewPartition = this.getChartViewPartition(viewId);
        if (!chartViewPartition) {
            return {};
        }
        // Ensure that the grouping index is registered
        // IMPORTANT: Given the index references planValidBucketIds table, we must refresh the index whenever valid ids change
        const timeZone = getSystemTimeZone();
        const todayPlainDate = getTodayPlainDate(timeZone, this.loggers.traceLogger);
        registerIndex(chartViewPartition, groupBy, (task)=>this.groupTaskSafely(groupBy, ()=>getTaskGroups(task, groupBy, this.configProvider, this.planValidBucketIds.get(viewId.partitionKey) ?? undefined, timeZone, todayPlainDate, this.planValidCfdValueTypes.get(viewId.partitionKey) ?? undefined)));
        const metricIndex = chartViewPartition.index(groupBy);
        return metricIndex.keys().reduce((acc, groupId)=>{
            acc[groupId] = this.getChartMetrics(metricIndex.partition(groupId));
            return acc;
        }, {});
    }
    // #endregion
    //#region HANDLERS
    handleAction(action) {
        let storeChanged = false;
        if (!this.shouldHandleAction(action)) {
            return;
        }
        // Capture planIds that may need label-usage recomputation BEFORE the slice changes,
        // since some actions (e.g. DeleteEntity*) remove the task before we can read its planId.
        const planIdsToRecomputeLabelCounts = this.configProvider().flights.EnableUsageBasedLabels ? this.collectPlanIdsForLabelCountRecompute(action) : [];
        switch(action.type){
            case ActionType.FetchEntityAction:
                storeChanged = this.taskSlice.handleFetchStarted(action.entityId);
                break;
            case ActionType.FetchEntityFailedAction:
                storeChanged = this.taskSlice.handleFetchFailed(action.entityId, action.response);
                break;
            case ActionType.FetchEntitySucceededAction:
                storeChanged = this.taskSlice.handleFetchSucceeded(action.entity.id, action.entity, action.forceOverrideStoredData);
                break;
            case ActionType.FetchEntityCollectionAction:
                storeChanged = this.taskSlice.handleCollectionFetchStarted({
                    name: action.relationship,
                    value: action.parentId
                });
                break;
            case ActionType.FetchEntityCollectionFailedAction:
                storeChanged = this.taskSlice.handleCollectionFetchFailed({
                    name: action.relationship,
                    value: action.parentId
                }, action.response);
                break;
            case ActionType.FetchEntityCollectionSucceededAction:
                storeChanged = this.handleFetchTaskCollectionSucceededAction(action);
                break;
            case ActionType.CreateEntityAction:
                storeChanged = this.taskSlice.handleCreateStarted(action.entity.id, action.entity);
                if (storeChanged) this.changeFreshMutationCount(action.entity.id, 1);
                break;
            case ActionType.CreateEntityFailedAction:
                storeChanged = this.taskSlice.handleCreateFailed(action.entity.id);
                if (storeChanged) this.changeFreshMutationCount(action.entity.id, -1);
                break;
            case ActionType.CreateEntitySucceededAction:
                storeChanged = this.taskSlice.handleCreateSucceeded(action.localId, action.entity, action.entity.id);
                if (storeChanged) {
                    this.taskViewFreshMutationCount.reKey(action.localId, action.entity.id);
                    this.taskTable.refresh(action.entity.id); // Refresh because the id has changed
                }
                break;
            case ActionType.DeleteEntityAction:
                storeChanged = this.taskSlice.handleDeleteStarted(action.entityId);
                break;
            case ActionType.DeleteEntityFailedAction:
                storeChanged = this.taskSlice.handleDeleteFailed(action.entityId, action.response, action.errorCode);
                break;
            case ActionType.DeleteEntitySucceededAction:
                storeChanged = this.taskSlice.handleDeleteSucceeded(action.entityId);
                break;
            case ActionType.UpdateEntityAction:
                storeChanged = this.taskSlice.handleUpdateStarted(action.entityId, action.updateKey, action.delta);
                if (storeChanged) this.changeFreshMutationCount(action.entityId, 1);
                break;
            case ActionType.UpdateEntityFailedAction:
                storeChanged = this.taskSlice.handleUpdateFailed(action.entityId, action.updateKey, action.response, action.errorCode);
                if (storeChanged) this.changeFreshMutationCount(action.entityId, -1);
                break;
            case ActionType.UpdateEntitySucceededAction:
                storeChanged = this.taskSlice.handleUpdateSucceeded(action.entity.id, action.updateKey, action.entity);
                break;
            case ActionType.UndoRedoUpdateEntitySucceededAction:
                storeChanged = this.taskSlice.handleUpdateSucceeded(action.entity.id, action.updateKey, action.entity, true);
                break;
            case ActionType.UnDeleteEntityAction:
                storeChanged = this.taskSlice.handleUndeleteStarted(action.entity.id, action.entity);
                break;
            case ActionType.UnDeleteEntityFailedAction:
                storeChanged = this.taskSlice.handleUndeleteFailed(action.entityId);
                break;
            case ActionType.UnDeleteEntitySucceededAction:
                storeChanged = this.taskSlice.handleUndeleteSucceeded(action.entity.id, action.entity);
                break;
            case ActionType.FetchLoopPageUrlForTaskAction:
                storeChanged = this.loopPageUrlSlice.handleFetchStarted(action.taskId);
                break;
            case ActionType.FetchLoopPageUrlForTaskFailedAction:
                storeChanged = this.loopPageUrlSlice.handleFetchFailed(action.taskId, action.response);
                break;
            case ActionType.FetchLoopPageUrlForTaskSucceededAction:
                storeChanged = this.loopPageUrlSlice.handleFetchSucceeded(action.taskId, action.loopPageUrl);
                break;
            case ActionType.UpdateBulkEditStatusAction:
                storeChanged = this.handleUpdateBulkEditStatusAction(action);
                break;
            case ActionType.UploadAttachmentForTaskAction:
                storeChanged = this.handleUploadAttachmentForTaskAction(action);
                break;
            case ActionType.UploadAttachmentForTaskFailedAction:
            case ActionType.UploadAttachmentForTaskSucceededAction:
                storeChanged = this.removeInProgressUploadForTask(action.taskId, action.session.id);
                break;
            case ActionType.SetTaskFilterAction:
                this.handleSetTaskFilterAction(action);
                this.resetFreshMutationCounts();
                storeChanged = true;
                break;
            case ActionType.SetSortCriteriaAction:
                this.handleSetSortCriteriaAction(action);
                storeChanged = true;
                break;
            case ActionType.SetTaskGroupingStateAction:
            case ActionType.NavigateToView:
                this.resetFreshMutationCounts();
                storeChanged = true;
                break;
            case ActionType.SetDefaultPlanAction:
                storeChanged = this.setDefaultPlanId(action.source, action.value);
                break;
            case ActionType.ClearDefaultPlanAction:
                storeChanged = this.clearDefaultPlanId(action.source);
                break;
            case ActionType.FetchDefaultPlanAction:
                storeChanged = this.setDefaultPlanStatus(action.source, true, StoreEntityState.Absent);
                break;
            case ActionType.FetchDefaultPlanFailedAction:
                storeChanged = this.setDefaultPlanStatus(action.source, false, StoreEntityState.UnableToFetch);
                break;
            case ActionType.SetFlaggedEmailPlanAction:
                this.flaggedEmailsPlanId = action.value;
                this.taskTable.refreshIndex(ViewIndex); // Refresh view index because view partitions could have changed
                storeChanged = true;
                break;
            case ActionType.DeltaSyncAction:
                storeChanged = this.taskSlice.handleDeltaSyncStarted({
                    name: action.relationship,
                    value: action.parentId
                });
                break;
            case ActionType.DeltaSyncSucceededAction:
                storeChanged = this.taskSlice.handleDeltaSyncSucceeded({
                    name: action.relationship,
                    value: action.parentId
                }, Object.fromEntries(action.deltas.map((item)=>[
                        item.entity.id,
                        item
                    ])), action.deltaLink);
                break;
            case ActionType.DeltaSyncFailedAction:
                storeChanged = this.taskSlice.handleDeltaSyncFailed({
                    name: action.relationship,
                    value: action.parentId
                }, action.response, action.errorCode);
                break;
            case ActionType.ReportValidBucketsAction:
                storeChanged = this.handleReportValidBucketsAction(action);
                break;
            case ActionType.ReportValidGoalsAction:
                storeChanged = this.handleReportValidGoalsAction(action);
                break;
            case ActionType.ReportValidCustomFieldDefinitionsAction:
                storeChanged = this.handleReportValidCustomFieldDefinitionsAction(action);
                break;
            case ActionType.SuspendEntityDeltaAction:
                storeChanged = this.taskSlice.handleSuspendDelta(action.entityId, action.deltaKey);
                if (storeChanged) this.changeFreshMutationCount(action.entityId, -1);
                break;
            case ActionType.ResumeEntityDeltaAction:
                storeChanged = this.taskSlice.handleResumeDelta(action.entityId, action.deltaKey);
                if (storeChanged) this.changeFreshMutationCount(action.entityId, 1);
                break;
            case ActionType.DropSuspendedDeltaAction:
                storeChanged = this.taskSlice.handleDropSuspendedDelta(action.entityId, action.deltaKey);
                break;
            case ActionType.PruneBaselineChainAction:
                storeChanged = this.taskSlice.removeBaselineChainEntry(action.entityId, action.deltaKey, action.prunePrefix);
                break;
            default:
                logUnreachableAction(this, this.loggers, action);
                return;
        }
        if (storeChanged) {
            for (const planId of planIdsToRecomputeLabelCounts){
                this.recomputeLabelUsageCountsForPlan(planId);
            }
            this.publishStoreChangedEvent();
        }
    }
    handleReportValidBucketsAction(action) {
        const { planId, validBuckets } = action;
        const idsUpdated = this.planValidBucketIds.set(planId, new Set(validBuckets.map((bucket)=>bucket.id)));
        if (idsUpdated) {
            // Refresh board and charts plan view partitions for the plan to reflect updated bucket grouping
            const planViewId = {
                name: ViewName.PlanPage,
                partitionKey: planId
            };
            for (const viewType of [
                ViewType.Board,
                ViewType.Charts
            ]){
                this.getTasksViewPartition(planViewId, viewType).refreshIndex(TaskGroupingOption.Bucket);
            }
            // Refresh plan partition's bucket index to reflect invalid buckets
            this.taskTable.index(QueryRelationship.TasksInPlan).partition(planId).refreshIndex(EntityType.Bucket);
            return true;
        }
        return false;
    }
    handleReportValidGoalsAction(action) {
        const { planId, validGoals } = action;
        const idsUpdated = this.planValidGoalIds.set(planId, new Set(validGoals.map((goal)=>goal.id)));
        if (idsUpdated) {
            // Refresh board and charts plan view partitions for the plan to reflect updated goal grouping
            const planViewId = {
                name: ViewName.PlanPage,
                partitionKey: planId
            };
            for (const viewType of [
                ViewType.Board,
                ViewType.Charts
            ]){
                this.getTasksViewPartition(planViewId, viewType).refreshIndex(TaskGroupingOption.Goal);
            }
            // Refresh the Goals view partition's goal index (uses EntityType.Goal, unlike the board/charts grouping index above)
            if (this.configProvider().flights.EnableFiltersInGoalsView) {
                this.getTasksViewPartition(planViewId, ViewType.Goals).refreshIndex(EntityType.Goal);
            }
            // Refresh plan partition's goal index to reflect invalid goals
            this.taskTable.index(QueryRelationship.TasksInPlan).partition(planId).refreshIndex(EntityType.Goal);
            return true;
        }
        return false;
    }
    handleReportValidCustomFieldDefinitionsAction(action) {
        const { planId, validCustomFieldDefinitions } = action;
        // Build maps for the new CFD data
        const newCfdValueTypes = new Map();
        const newChoiceDisplayNames = new Map();
        for (const cfd of validCustomFieldDefinitions){
            newCfdValueTypes.set(cfd.id, cfd.valueType);
            for (const choice of cfd.choiceOptions){
                newChoiceDisplayNames.set(choice.id, choice.displayName);
            }
        }
        // Collect affected CFD IDs before update for index refresh
        const oldCfdIds = this.planValidCfdValueTypes.get(planId)?.keys() ?? [];
        const allAffectedCfdIds = new Set([
            ...oldCfdIds,
            ...newCfdValueTypes.keys()
        ]);
        // Set maps — Table equality check naturally detects duplicates
        const cfdChanged = this.planValidCfdValueTypes.set(planId, newCfdValueTypes);
        const choiceChanged = this.planValidChoiceDisplayNames.set(planId, newChoiceDisplayNames);
        if (cfdChanged || choiceChanged) {
            // Refresh board view partition for custom field grouping indexes
            const planViewId = {
                name: ViewName.PlanPage,
                partitionKey: planId
            };
            const boardPartition = this.getTasksViewPartition(planViewId, ViewType.Board);
            for (const cfdId of allAffectedCfdIds){
                boardPartition.refreshIndex(toCustomFieldGroupingOption(cfdId));
            }
            return true;
        }
        return false;
    }
    shouldHandleAction(action) {
        if (isEntityAction(action)) {
            // Only handle entity actions for tasks
            return action.entityType === EntityType.Task;
        }
        return true; // Handle all other actions
    }
    /**
     * Collect plan ids whose label usage counts may need to be refreshed as a result of {@link action}.
     * Called BEFORE the slice mutates so that delete-style actions can still resolve their task's planId.
     *
     * Every single-task path routes through {@link planIdIfCategoriesChanged}: we extract the
     * "before" and "after" applied-category-id sets for the task and skip the recompute when they
     * are membership-equal. Bulk paths (collection fetch, delta sync) skip the per-task filter
     * because the recompute walks the whole plan anyway.
     */ collectPlanIdsForLabelCountRecompute(action) {
        switch(action.type){
            // --- Single-task: entity arriving where the store may already hold a prior version. ---
            case ActionType.FetchEntitySucceededAction:
            case ActionType.CreateEntitySucceededAction:
                {
                    const current = this.getTask(action.entity.id).data;
                    return this.planIdIfCategoriesChanged(action.entity.planId, current?.getAppliedCategoryIds() ?? [], action.entity.getAppliedCategoryIds());
                }
            // --- Single-task: optimistic add (no prior version in store). ---
            case ActionType.CreateEntityAction:
                return this.planIdIfCategoriesChanged(action.entity.planId, [], action.entity.getAppliedCategoryIds());
            case ActionType.CreateEntityFailedAction:
                return this.planIdIfCategoriesChanged(action.entity.planId, action.entity.getAppliedCategoryIds(), []);
            case ActionType.UnDeleteEntityAction:
            case ActionType.UnDeleteEntitySucceededAction:
                return this.planIdIfCategoriesChanged(action.entity.planId, [], action.entity.getAppliedCategoryIds());
            // --- Single-task: mutation (optimistic edit / server ack / rollback). ---
            case ActionType.UpdateEntityAction:
                {
                    // Cheap field-presence check first — most deltas don't carry appliedCategoryIds at all.
                    const deltaCategoryIds = action.delta.planView?.appliedCategoryIds;
                    if (deltaCategoryIds === undefined) return [];
                    const current = this.getTask(action.entityId).data;
                    if (!current) return [];
                    return this.planIdIfCategoriesChanged(current.planId, current.getAppliedCategoryIds(), deltaCategoryIds);
                }
            case ActionType.UpdateEntitySucceededAction:
                {
                    const current = this.getTask(action.entity.id).data;
                    return this.planIdIfCategoriesChanged(action.entity.planId, current?.getAppliedCategoryIds() ?? [], action.entity.getAppliedCategoryIds());
                }
            case ActionType.UpdateEntityFailedAction:
                {
                    const current = this.getTask(action.entityId).data;
                    const baseline = this.getTask(action.entityId, true).data;
                    const planId = current?.planId ?? baseline?.planId;
                    if (!planId) return [];
                    return this.planIdIfCategoriesChanged(planId, current?.getAppliedCategoryIds() ?? [], baseline?.getAppliedCategoryIds() ?? []);
                }
            // --- Single-task: removal. "After" is the empty set since the task is going away. ---
            case ActionType.DeleteEntityAction:
            case ActionType.DeleteEntityFailedAction:
            case ActionType.DeleteEntitySucceededAction:
                {
                    const current = this.getTask(action.entityId).data;
                    if (!current) return [];
                    return this.planIdIfCategoriesChanged(current.planId, current.getAppliedCategoryIds(), []);
                }
            case ActionType.UnDeleteEntityFailedAction:
                {
                    const current = this.getTask(action.entityId).data;
                    if (!current) return [];
                    return this.planIdIfCategoriesChanged(current.planId, current.getAppliedCategoryIds(), []);
                }
            // --- Bulk paths: recompute the affected plan(s) wholesale; per-task filtering not worth it. ---
            case ActionType.FetchEntityCollectionSucceededAction:
                return action.relationship === QueryRelationship.TasksInPlan ? [
                    action.parentId
                ] : [];
            case ActionType.DeltaSyncSucceededAction:
                {
                    const planIds = new Set();
                    if (action.relationship === QueryRelationship.TasksInPlan) {
                        planIds.add(action.parentId);
                    }
                    for (const item of action.deltas){
                        planIds.add(item.entity.planId);
                    }
                    return [
                        ...planIds
                    ];
                }
            default:
                return [];
        }
    }
    /** Returns `[planId]` only when {@link before} and {@link after} differ as multisets. */ planIdIfCategoriesChanged(planId, before, after) {
        return compareArraysEqualAsSet(before, after) ? [] : [
            planId
        ];
    }
    /**
     * Recompute the per-category usage counts for {@link planId} from the current task slice
     * and cache them. Caller must invoke this after the slice has been updated.
     */ recomputeLabelUsageCountsForPlan(planId) {
        const tasks = this.getTasksForPlan(planId).data ?? [];
        if (tasks.length === 0) {
            this.labelUsageCountsByPlan.delete(planId);
            return;
        }
        const counts = new Map();
        for (const task of tasks){
            for (const id of task.getAppliedCategoryIds()){
                counts.set(id, (counts.get(id) ?? 0) + 1);
            }
        }
        this.labelUsageCountsByPlan.set(planId, counts);
    }
    handleFetchTaskCollectionSucceededAction(action) {
        const todoTaskIds = this.taskSlice.getCollection({
            name: action.relationship,
            value: action.parentId
        }).data.filter((task)=>task.kind === TaskKind.ToDo).map((task)=>task.id);
        const accumulatedTaskIds = action.accumulatedEntityIds ?? [];
        /**
         * Prevent ToDo tasks from being overwritten because they are fetched from a different source and managed independently.
         *
         * Normally, when a slice accepts a collection fetch, it replaces the existing collection items with the new set to ensure
         * correctness. This works except for cases where we have have a collection with data from multiple sources.
         *
         * Example:
         * 1. Fetch todo tasks in MyDay ["TodoMyDay1", "TodoMyDay2"]
         * 2. Fetch planner tasks in MyDay ["PlannerMyDay1", "PlannerMyDay2"]
         * 3. The second fetch would replace the first fetch's tasks, resulting in only planner tasks being present in MyDay. Hence,
         * we explicitly mark the existing todo task id's for preservation.
         */ const taskIdsToPreserve = accumulatedTaskIds.concat(todoTaskIds);
        const storeChanged = this.taskSlice.handleCollectionFetchSucceeded({
            name: action.relationship,
            value: action.parentId
        }, action.nextLink == null, Object.fromEntries(action.entities.map((item)=>[
                item.id,
                item
            ])), taskIdsToPreserve, action.deltaLink, action.forceOverrideStoredData);
        // Apply order-hint comparator to the plan partition so items are always stored sorted.
        // Called once per plan — the partition maintains sort order on subsequent adds/removes/updates.
        if (storeChanged && action.relationship === QueryRelationship.TasksInPlan) {
            applyComparator(this.taskTable.index(QueryRelationship.TasksInPlan).partition(action.parentId), (task1, task2)=>compareByOrderHint(task2, task1, (task)=>task.planView.orderHints.get(TaskGroupingOption.None)));
        }
        return storeChanged;
    }
    handleSetTaskFilterAction(action) {
        const { viewId, filter } = action;
        let rootViewPartition;
        switch(viewId.name){
            case ViewName.MyDayPage:
                rootViewPartition = this.taskTable.index(ViewIndex).partition(MyDayViewPartition);
                break;
            case ViewName.MyTasksPage:
                rootViewPartition = this.taskTable.index(ViewIndex).partition(MyTasksViewPartition);
                break;
            case ViewName.PlanPage:
                rootViewPartition = this.taskTable.index(ViewIndex).partition(viewId.partitionKey);
                break;
            default:
                throw new Error(`RootTasksViewPartitionNotFound [View=${toViewIdString(viewId)}]`);
        }
        // Apply the filter to the root view partition so that it applies to all child partitions (grid, board, chart)
        // IMPORTANT NOTE: Filter application is dependent on local system time zone and current date and hence the results
        // will be outdated if the day or the local time zone changes. Hence we need additional mechanisms to ensure that
        // refresh happens in those scenarios. This is not super critical because users can always reopen the app or apply
        // a different filter to force a refresh, but it would be desirable to handle this automatically in future.
        const timeZone = getSystemTimeZone();
        const todayPlainDate = getTodayPlainDate(timeZone, this.loggers.traceLogger);
        const matchesFilter = (task)=>applyEntityFilter(task, filter, timeZone, todayPlainDate, this.planValidChoiceDisplayNames.get(viewId.partitionKey) ?? undefined, this.planValidCfdValueTypes.get(viewId.partitionKey) ?? undefined);
        // On the Plan page with task hierarchy enabled, the grid view keeps the ancestor chain of every
        // matched task so a nested match stays visible under its (non-matching) parents (e.g. A -> B -> C
        // with only B matching keeps A -> B)
        let gridAncestorIdsToKeep;
        if (this.configProvider().flights.EnableTaskHierarchy && viewId.name === ViewName.PlanPage && filter.filterCount > 0) {
            const matchedTasks = (this.getTasksForPlan(viewId.partitionKey).data ?? []).filter(matchesFilter);
            gridAncestorIdsToKeep = collectAncestorIds(matchedTasks, (taskId)=>this.getTask(taskId).data);
        }
        applyFilter(rootViewPartition, /**
             * The task should show up in the view if-
             * 1. It passes the applied filter OR
             * 2. It has fresh mutations (to avoid disappearing right after user action) OR
             * 3. It is an ancestor of a match in the grid view
             */ (task, path)=>matchesFilter(task) || // A task can only be freshly mutated while visible, so its ancestors are already in the view — no separate ancestor retention needed here.
            (this.taskViewFreshMutationCount.get(task.id) ?? 0) > 0 || gridAncestorIdsToKeep?.has(task.id) === true && path.includes(ViewType.Grid));
    }
    handleSetSortCriteriaAction(action) {
        const { viewId } = action;
        // WI #2608963: Implement sorting logic based on the sortCriteria object
        applyComparator(this.getTasksViewPartition(viewId, ViewType.Grid), (a, b)=>1);
    }
    handleUpdateBulkEditStatusAction(action) {
        if (this.isBulkEditOngoing === action.isBulkEditInProgress) {
            return false;
        }
        this.isBulkEditOngoing = action.isBulkEditInProgress;
        return true;
    }
    /**
     * Append a placeholder content link to the in-progress list for the given task.
     *
     * Idempotent: if a placeholder with the same `webUrl` (== upload session id) is already
     * tracked for this task, returns `false` so the dispatcher skips publishing a no-op
     * change event. This guards against accidental double-dispatch of the start action.
     */ handleUploadAttachmentForTaskAction(action) {
        const { taskId, contentLink } = action;
        const existing = this.inProgressUploadsByTask.get(taskId) ?? [];
        // Defensive: avoid duplicate placeholder for the same upload session
        if (existing.some((link)=>link.webUrl === contentLink.webUrl)) {
            return false;
        }
        // Immutable update so memoized consumers detect the change via reference equality
        this.inProgressUploadsByTask.set(taskId, [
            ...existing,
            contentLink
        ]);
        return true;
    }
    /**
     * Remove the placeholder content link tracked for a given task + upload session id.
     *
     * Used by both the succeeded and failed handlers — they share identical map mutations.
     * The semantic difference lives elsewhere: success additionally appends the real
     * {@link IContentLink} to `task.links` via the task update flow, while failure leaves
     * `task.links` untouched. Either way, this just clears the in-progress entry.
     *
     * @param taskId Id of the task whose placeholder should be removed
     * @param sessionId Upload session id (matches the placeholder's `webUrl`)
     * @returns `true` if a placeholder was actually removed; `false` for a no-op (entry
     *   never existed or was already cleared by an earlier action). The dispatcher uses
     *   this to decide whether to publish a store-changed event.
     */ removeInProgressUploadForTask(taskId, sessionId) {
        const existing = this.inProgressUploadsByTask.get(taskId);
        if (!existing) {
            // Task never had an in-flight upload (or was already cleared) — nothing to do.
            return false;
        }
        // Immutable filter: produce a new array so reference-equality consumers detect the change.
        const remainingUploads = existing.filter((link)=>link.webUrl !== sessionId);
        if (remainingUploads.length === existing.length) {
            // Idempotency guard: the matching placeholder was already removed by a prior
            // action (e.g. duplicate failure dispatch). Skip to avoid spurious re-renders.
            return false;
        }
        if (remainingUploads.length === 0) {
            // Last upload for this task finished — delete the map entry so subsequent reads
            // hit the shared `EmptyUploads` sentinel (referentially stable, no churn).
            this.inProgressUploadsByTask.delete(taskId);
        } else {
            this.inProgressUploadsByTask.set(taskId, remainingUploads);
        }
        return true;
    }
    // #endregion
    // #region HELPERS
    /**
     * Build a flat {@link IHierarchicalTasksResult} for the given tasks, where every task sits at
     * depth 0 (no parent-child nesting). Used by views that render a flat list rather than a tree.
     */ toFlatHierarchicalResult(tasks) {
        return {
            flattenedTasks: tasks,
            depthMap: new Map(tasks.map((task)=>[
                    task.id,
                    0
                ]))
        };
    }
    /**
     * Gets tasks from a partition in the lazily registered parent-task index for a plan.
     * @param planId ID of the plan containing the tasks.
     * @param partitionKey Root-task index key or parent task ID to query.
     * @returns Tasks in the requested parent-task index partition.
     */ getTasksForParentTaskIndex(planId, partitionKey) {
        const planPartition = this.taskTable.index(QueryRelationship.TasksInPlan).partition(planId);
        // Ensure that the plan partition has a parent-task index
        registerIndex(planPartition, ParentTaskIndex, (task)=>task.parentTaskId ?? RootTaskIndexKey);
        return planPartition.index(ParentTaskIndex).partition(partitionKey).items().map((item)=>item.value);
    }
    /**
     * Drops a date-sensitive grouping index when the local day has changed since it was registered, so the
     * caller's subsequent `registerIndex` rebuilds it against the current day. Needed because the grouping
     * index closure captures "today", and `registerIndex` is a no-op once the index exists while
     * `refreshIndex` reuses the stale closure — so date columns would otherwise freeze until restart.
     */ dropStaleDateGroupingIndex(partition, viewId, groupBy, today) {
        if (!this.isDateSensitiveGrouping(groupBy, viewId.partitionKey)) {
            return;
        }
        const trackingKey = `${toViewIdString(viewId)}|${groupBy}`;
        const todayString = today.toString();
        const registeredDay = this.dateGroupingIndexDayByKey.get(trackingKey);
        if (registeredDay === todayString) {
            return;
        }
        // First registration builds fresh; only drop on an actual day change.
        if (registeredDay !== undefined) {
            partition.dropIndex(groupBy);
        }
        this.dateGroupingIndexDayByKey.set(trackingKey, todayString);
    }
    /** Whether a grouping option's keys depend on today's date: Due date, Finish date, Start date, or a Date-type custom field. */ isDateSensitiveGrouping(groupBy, planPartitionKey) {
        if (groupBy === TaskGroupingOption.DueDate || groupBy === TaskGroupingOption.FinishDate || groupBy === TaskGroupingOption.StartDate) {
            return true;
        }
        const cfdId = resolveCustomFieldDefinitionIdFromGroupingOption(groupBy);
        if (cfdId == null) {
            return false;
        }
        return this.planValidCfdValueTypes.get(planPartitionKey)?.get(cfdId) === CustomFieldValueType.Date;
    }
    /**
     * Get the task partition for a specific view (and ensure it has the view indices).
     * For grid/board views, returns the filtered view partition to respect user filters.
     */ getTasksViewPartition(viewId, viewType) {
        // For grid/board views, use the filtered View partition
        const isPersonalView = viewId.name === ViewName.MyDayPage || viewId.name === ViewName.MyTasksPage;
        let taskPartition;
        const viewIndex = this.taskTable.index(ViewIndex);
        switch(viewId.name){
            case ViewName.MyDayPage:
                taskPartition = viewIndex.partition(MyDayViewPartition);
                break;
            case ViewName.MyTasksPage:
                taskPartition = viewIndex.partition(MyTasksViewPartition).index(MyTasksViewPivotIndex).partition(viewId.partitionKey);
                break;
            case ViewName.PlanPage:
                taskPartition = viewIndex.partition(viewId.partitionKey);
                break;
            default:
                throw new Error(`TasksViewPartitionNotFound [View=${toViewIdString(viewId)}]`);
        }
        // Ensure that the task partition has a index for the viewType and default comparator is applied
        if (registerIndex(taskPartition, viewType, (_)=>viewType)) {
            const taskViewPartition = taskPartition.index(viewType).partition(viewType);
            /**
             * Setup default comparator based on view type (only grid and board views have ordering,
             * charts view is purely metrics). Note that `applyComparator` must be called only while
             * setting up the index for the first time, otherwise it will be run every time the partition
             * is accessed via `getTasksViewPartition` which is unnecessary. This is unlike `registerIndex`
             * which can be freely called multiple times even on the read path since it is a no-op if
             * the index already exists.
             */ if (viewType === ViewType.People) {
                /**
                 * Team View is Timeline grouped by assignee, so it orders tasks with the same comparator
                 * `getTasksForTimelineView` applies. Lane packing sorts by start date but is stable, so
                 * tasks sharing a start date fall back to this order; without it they would take raw
                 * insertion order and could swap lanes between updates. Applying it to the partition
                 * (rather than re-sorting per read like Timeline does) keeps the ordering incremental.
                 */ applyComparator(taskViewPartition, getDefaultTasksSortComparator_PlanView());
            } else if (viewType === ViewType.Goals) {
                // Goals view groups by goal (TaskGroupingOption.None order hint, same as the unfiltered plan
                // partition used by getTasksForGoal), so it must sort with the same descending comparator to
                // keep task order stable when EnableFiltersInGoalsView is toggled on.
                applyComparator(taskViewPartition, (task1, task2)=>compareByOrderHint(task2, task1, (task)=>task.planView.orderHints.get(TaskGroupingOption.None)));
            } else if (viewType === ViewType.Grid || viewType === ViewType.Board || viewType === ViewType.Schedule) {
                applyComparator(taskViewPartition, this.getDefaultComparator(viewType, isPersonalView));
            }
        }
        return taskPartition.index(viewType).partition(viewType);
    }
    /**
     * Safe wrapper around getTasksViewPartition for chart views.
     * Returns null if the partition chain is not yet available (e.g., store is Absent during navigation).
     * Any error from the underlying partition access is caught, logged as an Error, and treated as an absent partition.
     */ getChartViewPartition(viewId) {
        try {
            return this.getTasksViewPartition(viewId, ViewType.Charts);
        } catch (e) {
            this.loggers.traceLogger.logTrace(0x1e0000cf /* tag_4aadp */ , TraceLevel.Error, `ChartViewPartitionUnavailable [ViewId=${toViewIdString(viewId)}] ${generateLogStringFromError(e)}`);
            return null;
        }
    }
    /**
     * Helper to get chart metrics from a partition
     */ getChartMetrics(partition) {
        // Ensure that there is ChartMetricIndex registered
        registerIndex(partition, ChartMetricIndex, (task)=>getTaskChartMetric(task));
        return Object.values(TaskChartMetric).reduce((acc, metric)=>{
            acc[metric] = partition.index(ChartMetricIndex).partition(metric).itemIds().length;
            return acc;
        }, {});
    }
    /**
     * Helper to get the default comparator for a given view type
     * @param viewType The view type (grid or board)
     * @param isPersonalView Whether the view is personal (My Day or My Tasks)
     */ getDefaultComparator(viewType, isPersonalView) {
        return (task1, task2, path)=>{
            // Example path for board group partition: task/plan/plan1/board/board/bucket/bucket1
            // Example path for grid partition: task/plan/plan1/grid/grid
            const groupBy = viewType === ViewType.Board ? path[path.length - 2] : TaskGroupingOption.None;
            // For DueDate and Priority grouping, sort by due date then name.
            if (groupBy === TaskGroupingOption.DueDate || groupBy === TaskGroupingOption.Priority) {
                return compareByDueDate(task1, task2) || compareByName(task1, task2);
            }
            // For StartDate grouping, sort by start date then name so the store's column order matches
            // the drop placeholder (compareByStartDate) — no per-StartDate orderHints are written.
            if (groupBy === TaskGroupingOption.StartDate) {
                return compareByStartDate(task1, task2) || compareByName(task1, task2);
            }
            // For Category grouping, sort by name instead of orderHint
            if (groupBy === TaskGroupingOption.Category) {
                return compareByName(task1, task2);
            }
            // For all other groupings, use orderHint-based sorting
            const orderHintAccessor = (task)=>{
                if (isPersonalView) {
                    return task.personalView.orderHints.get(groupBy);
                }
                const groupKey = viewType === ViewType.Board ? path[path.length - 1] : undefined;
                /**
                 * For Assignee grouping, we need to handle unassigned tasks by falling back to
                 * the "unassigned" order hint if the specific assignee hint is not present.
                 * The neighbor assignee could be from taskeditor or other view, which might not have orderhint per-assignee
                 */ if (groupBy === TaskGroupingOption.Assignee) {
                    return task.planView.orderHints.get(TaskGroupingOption.Assignee, groupKey) ?? task.planView.orderHints.get(TaskGroupingOption.Assignee, UnassignedTaskGroup);
                }
                return task.planView.orderHints.get(groupBy, groupKey);
            };
            return compareByOrderHint(task1, task2, orderHintAccessor);
        };
    }
    /** Helper to get the view partitions a task belongs to */ getViewsForTask(task) {
        const partitions = [];
        // MyDay partition
        if (task.personalView.isOnMyDay) {
            partitions.push(MyDayViewPartition);
        }
        // MyTasks partition (Because this depends upon private tasks and flagged emails plan ids, the view index needs to refresh when those change)
        if (this.isDefaultPlan(task.planId) || task.userAssignments.some((ua)=>ua.user.id === this.user.id) || task.planId === this.flaggedEmailsPlanId) {
            partitions.push(MyTasksViewPartition);
        }
        // Plan partitions (all tasks belong to their plan partition)
        partitions.push(task.planId);
        return partitions;
    }
    /** Helper to get the pivots a task in MyTasks partition belongs to */ getMyTaskPivotsForTask(task) {
        const partitions = [];
        if (this.isDefaultPlan(task.planId)) {
            partitions.push(MyTasksViewPivotType.All, MyTasksViewPivotType.PrivateTasks);
        }
        if (task.userAssignments.some((ua)=>ua.user.id === this.user.id)) {
            partitions.push(MyTasksViewPivotType.All, MyTasksViewPivotType.AssignedToMe);
        }
        if (task.planId === this.flaggedEmailsPlanId) {
            partitions.push(MyTasksViewPivotType.All, MyTasksViewPivotType.FlaggedEmails);
        }
        return partitions;
    }
    /** Helper to get the tasks data partition for a given view ID */ getTasksDataPartition(viewId) {
        switch(viewId.name){
            case ViewName.MyDayPage:
                return this.getMyDayTasks();
            case ViewName.MyTasksPage:
                switch(viewId.partitionKey){
                    case MyTasksViewPivotType.PrivateTasks:
                        return this.getTasksForPlan(PlannerDefaultPlanId);
                    case MyTasksViewPivotType.FlaggedEmails:
                        return this.getTasksForPlan(this.flaggedEmailsPlanId ?? "");
                    case MyTasksViewPivotType.All:
                    case MyTasksViewPivotType.AssignedToMe:
                    default:
                        return this.getTasksAssignedToUser(this.user.id);
                }
            case ViewName.PlanPage:
                return this.getTasksForPlan(viewId.partitionKey);
            default:
                throw new Error(`TasksDataPartitionNotFound [View=${toViewIdString(viewId)}]`);
        }
    }
    getDefaultPlanId(source) {
        return this.defaultPlanIds[source] ?? null;
    }
    isDefaultPlan(planId) {
        return Object.values(this.defaultPlanIds).includes(planId);
    }
    setDefaultPlanId(source, planId) {
        const identityChanged = this.defaultPlanIds[source] !== planId;
        const statusChanged = this.defaultPlanStatuses[source]?.isFetching !== false || this.defaultPlanStatuses[source]?.state !== StoreEntityState.Present;
        this.defaultPlanIds[source] = planId;
        this.defaultPlanStatuses[source] = {
            isFetching: false,
            state: StoreEntityState.Present,
            timestamp: Temporal.Now.instant()
        };
        if (identityChanged) {
            this.taskTable.refreshIndex(ViewIndex);
        }
        return identityChanged || statusChanged;
    }
    setDefaultPlanStatus(source, isFetching, state) {
        const current = this.defaultPlanStatuses[source];
        if (current?.isFetching === isFetching && current.state === state) {
            return false;
        }
        this.defaultPlanStatuses[source] = {
            isFetching,
            state,
            timestamp: Temporal.Now.instant()
        };
        return true;
    }
    clearDefaultPlanId(source) {
        // Stale-ID recovery broadcasts this action to stores that may already be clear, so deletion is intentionally idempotent.
        const identityChanged = this.defaultPlanIds[source] !== undefined;
        const statusChanged = this.defaultPlanStatuses[source] !== undefined;
        delete this.defaultPlanIds[source];
        delete this.defaultPlanStatuses[source];
        if (identityChanged) {
            this.taskTable.refreshIndex(ViewIndex);
        }
        return identityChanged || statusChanged;
    }
    /* Helper to change the fresh mutation count for a task */ changeFreshMutationCount(taskId, change) {
        this.taskViewFreshMutationCount.set(taskId, (this.taskViewFreshMutationCount.get(taskId) ?? 0) + change);
        this.taskTable.refresh(taskId); // Because of changed mutation count, the task visibility could have changed
    }
    /** Helper to reset the fresh mutation count for a task */ resetFreshMutationCounts() {
        this.taskTable.executeBatch(()=>{
            for (const taskId of this.taskViewFreshMutationCount.itemIds()){
                this.taskViewFreshMutationCount.delete(taskId);
                this.taskTable.refresh(taskId); // Refresh task as now it might be removed from the view
            }
        });
    }
    /** Helper to get the task entity table from the slice */ get taskTable() {
        return this.taskSlice.table();
    }
    /**
     * Creates an instance of TaskStore
     * @param user The current user
     * @param loggers The logger context
     * @param configProvider The config provider for flight checks
     */ constructor(user, loggers, configProvider){
        super(), this.user = user, this.loggers = loggers, this.configProvider = configProvider, /**
     * Task table partition structure
     *
     * ```
     * ☶ Root Table: All Tasks
     * ├── TasksInMyDayOfUser
     * │   └── ☶ User1 (Only a single partition for current user)
     * │
     * ├── TasksAssignedToUser
     * │   ├── ☶ User1
     * │   └── ☶ User2
     * │
     * ├── TasksInPlan (UNFILTERED - used by charts to show all tasks)
     * │   ├── ☶ Plan1
     * │   │   ├── Metrics // <-- Metrics read directly from here unfiltered
     * │   │   │
     * │   │   ├── Bucket // <-- Charts can group by bucket at plan level
     * │   │   │   ├── ☶ Bucket1
     * │   │   │   │   └── [Metrics]
     * │   │   │   └── ☶ Bucket2
     * │   │   │
     * │   │   ├── Goal // <-- Charts can group by goal at plan level
     * │   │   │   ├── ☶ Goal1
     * │   │   │   │   └── [Metrics]
     * │   │   │   └── ☶ Goal2
     * │   │   │
     * │   │   ├── ParentTask // <-- Groups root tasks and child tasks by parentTaskId
     * │   │   │   ├── ☶ Root
     * │   │   │   │   └── [Root tasks]
     * │   │   │   ├── ☶ ParentTask1
     * │   │   │   │   └── [Child tasks]
     * │   │   │   └── ☶ ParentTask2
     * │   │   │
     * │   │   └── [Priority, Assignee] // <-- Charts can group by these at plan level
     * │   │
     * │   └── ☶ Plan2
     * │
     * └── View (FILTERED - used by grid/board/goals views)
     *     ├── ☶ MyDay // <-- MyDay view filter applied at this node so that it applies to all view types
     *     │   └── [Grid, Board]
     *     │
     *     ├── ☶ MyTasks // <-- MyTasks view filter applied at this node so that it applies to all pivots and their view types
     *     │   └── Pivot
     *     │       ├── ☶ All
     *     │       ├── ☶ PrivateTasks
     *     │       ├── ☶ AssignedToMe
     *     │       └── ☶ FlaggedEmails
     *     │           └── [Grid, Board]
     *     │
     *     ├── ☶ Plan1
     *     └── ☶ Plan2 // <-- Plan level task filter applied at this node so that it applies to grid, board, and goals view types
     *         │
     *         ├── Grid
     *         │   └── ☶ Grid  // <-- Grid comparator applied at this node
     *         │
     *         ├── Goals // <-- EnableFiltersInGoalsView flight must be on for this to be present
     *         |   └── ☶ Goals  // <-- Goals view comparator applied at this node (descending order hint, same as TasksInPlan)
     *         |           ├── ☶ Goal 1
     *         |           └── ☶ Goal 2
     *         └── Board
     *             └── ☶ Board  // <-- Board comparator applied at this node
     *                 ├── Bucket
     *                 │   ├── ☶ Bucket 1
     *                 │   └── ☶ Bucket 2
     *                 │
     *                 ├── Goal
     *                 │   ├── ☶ Goal 1
     *                 │   └── ☶ Goal 2
     *                 │
     *                 └── [DueDate, Status, Assignee, Priority]
     *
     * Note: Chart data is NO LONGER retrieved from the View partition to avoid filter contamination.
     * Charts now read directly from the Plan partition to ensure they always show complete data.
     * ```
     */ this.taskSlice = new EntitySlice(TableName.Tasks, {
            /** Index for serving tasks-in-my-day data queries */ [QueryRelationship.TasksInMyDayOfUser]: (task)=>task.personalView.isOnMyDay ? this.user.id : undefined,
            /** Index for serving tasks-for-assignee data queries */ [QueryRelationship.TasksAssignedToUser]: (task)=>task.userAssignments.map((ua)=>ua.user.id),
            /** Index for serving tasks-in-plan data queries  */ [QueryRelationship.TasksInPlan]: (task)=>task.planId,
            /** Index for serving all view queries */ [ViewIndex]: (task)=>this.getViewsForTask(task)
        }, this.configProvider), /**
     * Tracks how many fresh mutations (create/update operations) have been applied to a task
     * during the current view session. A task is temporarily exempted from view filters while
     * it has a non-zero count here, to prevent it from disappearing immediately after user
     * interaction.
     *
     * This is important because multiple mutations can be in-flight for the same task.
     * Example:
     *   Create → Update (due date) → Update (assignee)
     *
     * We increment the count for each successful mutation so that a single failure does not
     * prematurely clear the exemption. The exemption is cleared only when:
     *  1. All in-flight mutations fail (task returns to its original state and remains visible)
     *  2. The user navigates away from the view
     *  3. Filters or grouping are changed
     */ this.taskViewFreshMutationCount = new Table("taskViewFreshMutationCount"), /**
     * Holds the valid bucket IDs for each plan to support correct task grouping by bucket.
     * If any id is encountered that is not in this set, it is grouped under "no bucket" group.
     */ this.planValidBucketIds = new Table("planValidBucketIds", (set1, set2)=>{
            if (set1.size !== set2.size) {
                return false;
            }
            for (const id of set1){
                if (!set2.has(id)) {
                    return false;
                }
            }
            return true;
        }), /**
     * Holds the valid goal IDs for each plan to support correct task-goal associations.
     * Only tasks associated with goals in this set are considered valid.
     */ this.planValidGoalIds = new Table("planValidGoalIds", (set1, set2)=>{
            if (set1.size !== set2.size) {
                return false;
            }
            for (const id of set1){
                if (!set2.has(id)) {
                    return false;
                }
            }
            return true;
        }), /**
     * Stores a Map from CFD ID → CustomFieldValueType per plan.
     * Used to determine the correct grouping behavior for tasks missing a
     * custom field value (e.g., Date CFDs group under "No date").
     */ this.planValidCfdValueTypes = new Table("planValidCfdValueTypes", (a, b)=>{
            if (a.size !== b.size) return false;
            for (const [key, value] of a){
                if (b.get(key) !== value) return false;
            }
            return true;
        }), /**
     * Stores a Map from choice ID → display name per plan.
     * Used to enable keyword filtering on SingleChoiceText custom field values
     * by matching against the human-readable display name instead of the internal option value.
     */ this.planValidChoiceDisplayNames = new Table("planValidChoiceDisplayNames", (a, b)=>{
            if (a.size !== b.size) return false;
            for (const [key, value] of a){
                if (b.get(key) !== value) return false;
            }
            return true;
        }), /** Grouping options already reported as unsupported by this store, so a per-task index rebuild logs once. */ this.loggedGroupingFailures = new Set(), /** Local ISO day (YYYY-MM-DD) each date-sensitive grouping index was registered with, keyed `${viewId}|${groupBy}`; used to detect a day rollover and rebuild the index. */ this.dateGroupingIndexDayByKey = new Map(), /** Flag to indicate if bulk edit is in progress */ this.isBulkEditOngoing = false, // Known plan id's for the user (these serve the pivots in MyTasksView)
        this.defaultPlanIds = {}, this.defaultPlanStatuses = {}, // Slice for storing Loop page URLs for tasks
        this.loopPageUrlSlice = new Slice(TableName.LoopPageUrl, {}, this.configProvider), /**
     * In-progress local-file attachment uploads, keyed by task id.
     *
     * Each entry is a placeholder {@link IContentLink} that the task editor renders as an
     * "uploading" row while the file transfer is running. The placeholder's `webUrl` is set
     * to the upload session id (a {@link LocalIdGenerator} temp id) — that's the correlation
     * key used by the success/failure handlers to find and remove the right entry.
     *
     * Lifecycle:
     *  - Added on `UploadAttachmentForTaskAction` (start)
     *  - Removed on `UploadAttachmentForTaskSucceededAction` / `...FailedAction` (terminal)
     *
     * On success, the real {@link IContentLink} is appended to `task.links` and shares the
     * placeholder's `id`, so the row keyed by `id` morphs in place rather than unmount/remount.
     */ this.inProgressUploadsByTask = new Map(), /**
     * Per-plan pre-aggregated label usage counts: planId -> (categoryId -> count of tasks
     * referencing that category in that plan). Mirrors the per-plan counts UNO maintains
     * in `AggregatesStore.getLabelUsageCountsForBasicPlan` so the label picker can sort
     * already-used labels to the top with an O(1) read instead of scanning all tasks at
     * render time.
     *
     * Updated incrementally by recomputing for the affected plan(s) on every task action
     * that can change task↔category linkage (see {@link collectPlanIdsForLabelCountRecompute}).
     * Reference stability: a new Map is allocated on each recompute — downstream consumers
     * should treat the map as immutable.
     */ this.labelUsageCountsByPlan = new Map();
        // Pre-register a sub-index for MyTasksView so that we have a task partition per pivot
        const myTasksViewPartition = this.taskTable.index(ViewIndex).partition(MyTasksViewPartition);
        registerIndex(myTasksViewPartition, MyTasksViewPivotIndex, (task)=>this.getMyTaskPivotsForTask(task));
    }
}
/**
     * Shared frozen sentinel returned by {@link getInProgressUploadsForTask} when a task has
     * no uploads in flight. Reused across calls for referential stability so memoized
     * consumers (e.g. React selectors) don't re-render on every action.
     */ TaskStore.EmptyUploads = Object.freeze([]);
/**
     * Shared frozen sentinel returned by {@link getLabelUsageCountsForPlan} when a plan has
     * no tracked tasks. Reused across calls for referential stability.
     */ TaskStore.EmptyLabelUsageCounts = Object.freeze(new Map());
